<template>
  <div>
    <slot
      name="trigger"
      :generate-spreadsheet="generateSpreadsheet"
      :start="startDate"
      :end="endDate"
    />
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import { mapState } from "vuex";

export default {
  name: "SpreadsheetGenerator",
  props: {
    startDate: {
      type: String,
      required: true,
    },
    endDate: {
      type: String,
      required: true,
    },
    fileSuffix: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState("Updates", ["monthlyRespondDates", "monthlyEffectiveDates", "monthlyApplicableDates", "monthlyReportingDates"]),
  },
  methods: {
    async generateSpreadsheet(filters) {
      let urlSuffix = "";
      try {
        if (filters.queryParams) {
          urlSuffix += `&${filters.queryParams}`;
        }
        await Promise.all([
          this.$store
            .dispatch("Updates/getViewData", {
              url: `/horizon-scanning/list?limit=150&filter[deadlineMinDate]=${this.startDate}&filter[deadlineMaxDate]=${this.endDate}${urlSuffix}&`,
            })
            .then(({ data }) => {
              this.$store.commit("Updates/setData", {
                label: "monthlyRespondDates",
                data,
              });
            })
            .catch((error) => console.error(error)),

          this.$store
            .dispatch("Updates/getViewData", {
              url: `/horizon-scanning/list?limit=150&filter[effectiveMinDate]=${this.startDate}&filter[effectiveMaxDate]=${this.endDate}${urlSuffix}&`,
            })
            .then(({ data }) => {
              this.$store.commit("Updates/setData", {
                label: "monthlyEffectiveDates",
                data,
              });
            })
            .catch((error) => console.error(error)),

          this.$store
            .dispatch("Updates/getViewData", {
              url: `/horizon-scanning/list?limit=150&filter[applicableMinDate]=${this.startDate}&filter[applicableMaxDate]=${this.endDate}${urlSuffix}&`,
            })
            .then(({ data }) => {
              this.$store.commit("Updates/setData", {
                label: "monthlyApplicableDates",
                data,
              });
            })
            .catch((error) => console.error(error)),

          this.$store
            .dispatch("Updates/getViewData", {
              url: `/horizon-scanning/list?limit=150&filter[reportingMinDate]=${this.startDate}&filter[reportingMaxDate]=${this.endDate}${urlSuffix}&`,
            })
            .then(({ data }) => {
              this.$store.commit("Updates/setData", {
                label: "monthlyReportingDates",
                data,
              });
            })
            .catch((error) => console.error(error)),
        ]);

        // Create a new workbook
        const workbook = new ExcelJS.Workbook();
        this.buildRespondBySheet(workbook);
        this.buildEffectiveBySheet(workbook);
        this.buildApplicableBySheet(workbook);
        this.buildReportingBySheet(workbook);

        // Generate the spreadsheet
        const buffer = await workbook.xlsx.writeBuffer();

        // Create a Blob from the buffer
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Create a download link and trigger the download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute(
          "download",
          `regulatory-deadlines-${this.fileSuffix}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error generating spreadsheet:", error);
      }
    },

    buildRespondBySheet(workbook) {
      const worksheet = workbook.addWorksheet("Monthly Respond by dates");
      // Add data to the worksheet
      worksheet.columns = [
        { header: "Id", key: "id", width: 15 },
        { header: "Title", key: "title", width: 80 },
        { header: "Body", key: "body", width: 100 },
        { header: "Link", key: "link", width: 100 },
        { header: "Links to references", key: "references", width: 100 },
        { header: "Released Date", key: "releasedDate", width: 15 },
        { header: "Response Deadline", key: "deadline", width: 20 },
        { header: "Date Published", key: "publishedDate", width: 15 },
        { header: "Classification", key: "classification", width: 15 },
        { hearer: "Terms", key: "terms", width: 75 },
        { header: "Authorities", key: "authorities", width: 60 },
        { header: "Document Types", key: "documentTypes", width: 25 },
        { header: "Featured Topics", key: "featuredTopics", width: 25 },
        { header: "Jurisdiction", key: "jurisdiction", width: 25 },
        { header: "Specialisms", key: "specialisms", width: 25 },
        {
          header: "Products and Services",
          key: "productsAndServices",
          width: 30,
        },
      ];

      this.monthlyRespondDates.forEach((item) => {
        worksheet.addRow({
          id: item.id,
          title: item.title,
          body: item.body,
          link: this.$config.clientURL + item.url,
          references: item.field_links_export.length
            ? item.field_links_export.map((link) => link.url).join("\n")
            : "",
          releaseDate: item.releasedDate,
          deadline: item.responseDeadline,
          publishedDate: item.datePublished,
          classification: item.classification,
          terms: item.tags.length
            ? item.tags.map((tag) => tag.title).join(", ")
            : "",
          authorities: item.authorities.length
            ? item.authorities.map((authority) => authority.title).join("\n")
            : "",
          documentTypes: item.documentTypes.length
            ? item.documentTypes.map((type) => type.title).join("\n")
            : "",
          featuredTopics: item.featuredTopics.length
            ? item.featuredTopics.map((topic) => topic.title).join("\n")
            : "",
          jurisdiction: item.jurisdictions.length
            ? item.jurisdictions
              .map((jurisdiction) => jurisdiction.title)
              .join("\n")
            : "",
          specialisms: item.specialisms.length
            ? item.specialisms.map((specialism) => specialism.title).join("\n")
            : "",
          productsAndServices: item.productsAndServices.length
            ? item.productsAndServices
              .map((product) => product.title)
              .join("\n")
            : "",
        });
      });

      worksheet.getRow(1).font = { bold: true };

      worksheet.eachRow({ includeEmpty: true, skipHeader: true }, (row) => {
        if (row.getCell("body").value) {
          row.getCell("body").value = row
            .getCell("body")
            .value.replace(/(<([^>]+)>)/gi, "");
        }
      });

      worksheet.eachRow((row) => {
        const contentHeight =
          row.getCell("body").value.split("\n").length * 15 + 10;
        row.height = contentHeight;
        row.alignment = { vertical: "top", wrapText: true };
      });
    },

    buildEffectiveBySheet(workbook) {
      const worksheet = workbook.addWorksheet("Monthly Effective by dates");

      // Add data to the worksheet
      worksheet.columns = [
        { header: "Id", key: "id", width: 15 },
        { header: "Title", key: "title", width: 80 },
        { header: "Body", key: "body", width: 100 },
        { header: "Link", key: "link", width: 100 },
        { header: "Links to references", key: "references", width: 100 },
        { header: "Released Date", key: "releasedDate", width: 15 },
        { header: "Effective Date", key: "effectiveDate", width: 20 },
        { header: "Date Published", key: "publishedDate", width: 15 },
        { header: "Classification", key: "classification", width: 15 },
        { hearer: "Terms", key: "terms", width: 75 },
        { header: "Authorities", key: "authorities", width: 60 },
        { header: "Document Types", key: "documentTypes", width: 25 },
        { header: "Featured Topics", key: "featuredTopics", width: 25 },
        { header: "Jurisdiction", key: "jurisdiction", width: 25 },
        { header: "Specialisms", key: "specialisms", width: 25 },
        {
          header: "Products and Services",
          key: "productsAndServices",
          width: 30,
        },
      ];

      this.monthlyEffectiveDates.forEach((item) => {
        worksheet.addRow({
          id: item.id,
          title: item.title,
          body: item.body,
          link: this.$config.clientURL + item.url,
          references: item.field_links_export.length
            ? item.field_links_export.map((link) => link.url).join("\n")
            : "",
          releaseDate: item.releasedDate,
          effectiveDate: item.effectiveDate,
          publishedDate: item.datePublished,
          classification: item.classification,
          terms: item.tags.length
            ? item.tags.map((tag) => tag.title).join(", ")
            : "",
          authorities: item.authorities.length
            ? item.authorities.map((authority) => authority.title).join("\n")
            : "",
          documentTypes: item.documentTypes.length
            ? item.documentTypes.map((type) => type.title).join("\n")
            : "",
          featuredTopics: item.featuredTopics.length
            ? item.featuredTopics.map((topic) => topic.title).join("\n")
            : "",
          jurisdiction: item.jurisdictions.length
            ? item.jurisdictions
              .map((jurisdiction) => jurisdiction.title)
              .join("\n")
            : "",
          specialisms: item.specialisms.length
            ? item.specialisms.map((specialism) => specialism.title).join("\n")
            : "",
          productsAndServices: item.productsAndServices.length
            ? item.productsAndServices
              .map((product) => product.title)
              .join("\n")
            : "",
        });
      });

      worksheet.getRow(1).font = { bold: true };

      worksheet.eachRow({ includeEmpty: true, skipHeader: true }, (row) => {
        if (row.getCell("body").value) {
          row.getCell("body").value = row
            .getCell("body")
            .value.replace(/(<([^>]+)>)/gi, "");
        }
      });

      worksheet.eachRow((row) => {
        const contentHeight =
          row.getCell("body").value.split("\n").length * 15 + 10;
        row.height = contentHeight;
        row.alignment = { vertical: "top", wrapText: true };
      });
    },

    buildApplicableBySheet(workbook) {
      const worksheet = workbook.addWorksheet("Monthly Applicable by dates");

      // Add data to the worksheet
      worksheet.columns = [
        { header: "Id", key: "id", width: 15 },
        { header: "Title", key: "title", width: 80 },
        { header: "Body", key: "body", width: 100 },
        { header: "Link", key: "link", width: 100 },
        { header: "Links to references", key: "references", width: 100 },
        { header: "Released Date", key: "releasedDate", width: 15 },
        { header: "Applicable Date", key: "applicableDate", width: 20 },
        { header: "Date Published", key: "publishedDate", width: 15 },
        { header: "Classification", key: "classification", width: 15 },
        { hearer: "Terms", key: "terms", width: 75 },
        { header: "Authorities", key: "authorities", width: 60 },
        { header: "Document Types", key: "documentTypes", width: 25 },
        { header: "Featured Topics", key: "featuredTopics", width: 25 },
        { header: "Jurisdiction", key: "jurisdiction", width: 25 },
        { header: "Specialisms", key: "specialisms", width: 25 },
        {
          header: "Products and Services",
          key: "productsAndServices",
          width: 30,
        },
      ];

      this.monthlyApplicableDates.forEach((item) => {
        worksheet.addRow({
          id: item.id,
          title: item.title,
          body: item.body,
          link: this.$config.clientURL + item.url,
          references: item.field_links_export.length
            ? item.field_links_export.map((link) => link.url).join("\n")
            : "",
          releaseDate: item.releasedDate,
          applicableDate: item.applicableDate,
          publishedDate: item.datePublished,
          classification: item.classification,
          terms: item.tags.length
            ? item.tags.map((tag) => tag.title).join(", ")
            : "",
          authorities: item.authorities.length
            ? item.authorities.map((authority) => authority.title).join("\n")
            : "",
          documentTypes: item.documentTypes.length
            ? item.documentTypes.map((type) => type.title).join("\n")
            : "",
          featuredTopics: item.featuredTopics.length
            ? item.featuredTopics.map((topic) => topic.title).join("\n")
            : "",
          jurisdiction: item.jurisdictions.length
            ? item.jurisdictions
              .map((jurisdiction) => jurisdiction.title)
              .join("\n")
            : "",
          specialisms: item.specialisms.length
            ? item.specialisms.map((specialism) => specialism.title).join("\n")
            : "",
          productsAndServices: item.productsAndServices.length
            ? item.productsAndServices
              .map((product) => product.title)
              .join("\n")
            : "",
        });
      });

      worksheet.getRow(1).font = { bold: true };

      worksheet.eachRow({ includeEmpty: true, skipHeader: true }, (row) => {
        if (row.getCell("body").value) {
          row.getCell("body").value = row
            .getCell("body")
            .value.replace(/(<([^>]+)>)/gi, "");
        }
      });

      worksheet.eachRow((row) => {
        const contentHeight =
          row.getCell("body").value.split("\n").length * 15 + 10;
        row.height = contentHeight;
        row.alignment = { vertical: "top", wrapText: true };
      });
    },

    buildReportingBySheet(workbook) {
      const worksheet = workbook.addWorksheet("Monthly Reporting by deadlines");

      // Add data to the worksheet
      worksheet.columns = [
        { header: "Id", key: "id", width: 15 },
        { header: "Title", key: "title", width: 80 },
        { header: "Body", key: "body", width: 100 },
        { header: "Link", key: "link", width: 100 },
        { header: "Links to references", key: "references", width: 100 },
        { header: "Released Date", key: "releasedDate", width: 15 },
        { header: "Reporting Deadline", key: "reportingDeadline", width: 20 },
        { header: "Date Published", key: "publishedDate", width: 15 },
        { header: "Classification", key: "classification", width: 15 },
        { hearer: "Terms", key: "terms", width: 75 },
        { header: "Authorities", key: "authorities", width: 60 },
        { header: "Document Types", key: "documentTypes", width: 25 },
        { header: "Featured Topics", key: "featuredTopics", width: 25 },
        { header: "Jurisdiction", key: "jurisdiction", width: 25 },
        { header: "Specialisms", key: "specialisms", width: 25 },
        {
          header: "Products and Services",
          key: "productsAndServices",
          width: 30,
        },
      ];

      this.monthlyReportingDates.forEach((item) => {
        worksheet.addRow({
          id: item.id,
          title: item.title,
          body: item.body,
          link: this.$config.clientURL + item.url,
          references: item.field_links_export.length
            ? item.field_links_export.map((link) => link.url).join("\n")
            : "",
          releaseDate: item.releasedDate,
          reportingDeadline: item.reportingDeadline,
          publishedDate: item.datePublished,
          classification: item.classification,
          terms: item.tags.length
            ? item.tags.map((tag) => tag.title).join(", ")
            : "",
          authorities: item.authorities.length
            ? item.authorities.map((authority) => authority.title).join("\n")
            : "",
          documentTypes: item.documentTypes.length
            ? item.documentTypes.map((type) => type.title).join("\n")
            : "",
          featuredTopics: item.featuredTopics.length
            ? item.featuredTopics.map((topic) => topic.title).join("\n")
            : "",
          jurisdiction: item.jurisdictions.length
            ? item.jurisdictions
              .map((jurisdiction) => jurisdiction.title)
              .join("\n")
            : "",
          specialisms: item.specialisms.length
            ? item.specialisms.map((specialism) => specialism.title).join("\n")
            : "",
          productsAndServices: item.productsAndServices.length
            ? item.productsAndServices
              .map((product) => product.title)
              .join("\n")
            : "",
        });
      });

      worksheet.getRow(1).font = { bold: true };

      worksheet.eachRow({ includeEmpty: true, skipHeader: true }, (row) => {
        if (row.getCell("body").value) {
          row.getCell("body").value = row
            .getCell("body")
            .value.replace(/(<([^>]+)>)/gi, "");
        }
      });

      worksheet.eachRow((row) => {
        const contentHeight =
          row.getCell("body").value.split("\n").length * 15 + 10;
        row.height = contentHeight;
        row.alignment = { vertical: "top", wrapText: true };
      });
    },
  },
};
</script>
